import { Form, Formik, FormikHelpers } from "formik";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { useCallback } from "react";

import { Card, CardContent, CardHeader } from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import {
  tenantEditCsvUploadLimit,
  tenantEditCsvUploadLimitVariables
} from "src/graphql/generated/tenantEditCsvUploadLimit";
import { EDIT_TENANT_CSV_UPLOAD_LIMIT } from "src/graphql/tenant";
import useMutation from "src/util/useMutation";
import yup from "src/util/yup";

import { numberAsStringField } from "./common";

type Values = { limitMb: string; limitRows: string };

export default function UploadLimit(): JSX.Element {
  const tenant = useTenant()!;
  const snackbar = useSnackbar();

  const [updateLimit] = useMutation<
    tenantEditCsvUploadLimit,
    tenantEditCsvUploadLimitVariables
  >(EDIT_TENANT_CSV_UPLOAD_LIMIT, {
    onErrorSnackbarMessage: "Failed to update upload limit."
  });

  const handleSubmit = useCallback(
    async (values: Values, formikHelpers: FormikHelpers<Values>) => {
      const { errors, data } = await updateLimit({
        variables: {
          newLimitMb: values.limitMb ? _.parseInt(values.limitMb) : null,
          newLimitRows: values.limitRows ? _.parseInt(values.limitRows) : null
        }
      });

      if (!errors) {
        snackbar.enqueueSnackbar("Organization updated.", { variant: "info" });
        formikHelpers.resetForm({
          values: {
            limitMb: numberAsStringField(
              data!.tenantEditCsvUploadLimit.csvUploadLimitMb
            ),
            limitRows: numberAsStringField(
              data!.tenantEditCsvUploadLimit.csvUploadLimitRows
            )
          }
        });
      }
    },
    [updateLimit, snackbar]
  );

  return (
    <Card>
      <CardHeader title="Upload Limit" />
      <CardContent>
        <Formik
          initialValues={{
            limitMb: numberAsStringField(tenant.csvUploadLimitMb),
            limitRows: numberAsStringField(tenant.csvUploadLimitRows)
          }}
          onSubmit={handleSubmit}
          validationSchema={yup.object().shape({
            limitMb: yup.number().nullable().positive().integer(),
            limitRows: yup.number().nullable().positive().integer()
          })}
        >
          {({ isSubmitting, dirty }) => (
            <Form>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="CSV Upload Size Limit in MB (leave blank to use system default)"
                name="limitMb"
                disabled={isSubmitting}
              />
              <FormikTextField
                fullWidth
                label="CSV Upload Row Limit (leave blank to use system default)"
                name="limitRows"
                disabled={isSubmitting}
              />
              <SubmitButton
                type="submit"
                disabled={!dirty}
                isSubmitting={isSubmitting}
                submitText="Update"
                loadingText="Updating..."
              />
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
}
